import { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import QRCode from 'qrcode'
import { Button } from '@astryxdesign/core/Button'
import { Dialog } from '@astryxdesign/core/Dialog'
import { DialogHeader } from '@astryxdesign/core/Dialog'
import { Stack } from '@astryxdesign/core/Stack'
import { Text } from '@astryxdesign/core/Text'
import StoreBadges from '../../components/StoreBadges'
import {
    APP_STORE_URL, PLAY_STORE_URL, openInApp, platformOf, universalLessonLink,
} from './bookingLink'

// "Réserver dans l'app" for one créneau. What it does depends on where it is
// tapped - see bookingLink.js for why a plain link to /app/lessons would not
// open the app from this page.
export function BookingButton({ seriesId, isFull }) {
    const [dialog, setDialog] = useState(null) // null | 'qr' | 'install'
    const [qr, setQr] = useState('')
    const cancelRef = useRef(null)

    useEffect(() => () => { if (cancelRef.current) cancelRef.current() }, [])

    const onClick = useCallback(async () => {
        const platform = platformOf(navigator.userAgent, navigator.maxTouchPoints || 0)

        if (platform === 'desktop') {
            try {
                setQr(await QRCode.toDataURL(universalLessonLink(seriesId), { width: 240, margin: 2, errorCorrectionLevel: 'M' }))
            } catch (e) {
                setQr('')
            }
            setDialog('qr')
            return
        }

        if (cancelRef.current) cancelRef.current()
        cancelRef.current = openInApp(seriesId, () => setDialog('install'))
    }, [seriesId])

    const close = useCallback(() => setDialog(null), [])

    return (
        <>
            <Button
                variant={isFull ? 'secondary' : 'primary'}
                label={isFull ? "Voir dans l'app" : "Réserver dans l'app"}
                onClick={onClick}
            />
            <Dialog isOpen={dialog !== null} onOpenChange={open => { if (!open) close() }} width={380} padding={6}>
                {dialog === 'qr' && (
                    <>
                        <DialogHeader title="Ouvrir ce cours sur votre téléphone" onOpenChange={close} />
                        <Stack gap={4} hAlign="center" paddingBlock={4}>
                            {qr && (
                                <img
                                    src={qr}
                                    alt="QR code du cours"
                                    style={{ width: '240px', height: '240px' }}
                                />
                            )}
                            <Text as="p" type="supporting" justify="center">
                                Scannez ce code avec l'appareil photo de votre téléphone : le cours
                                s'ouvre dans l'application Padelr, où vous pouvez vous inscrire.
                            </Text>
                            <Text as="p" type="supporting" justify="center">
                                Pas encore l'application ?
                            </Text>
                            <StoreBadges appStoreURL={APP_STORE_URL} playStoreURL={PLAY_STORE_URL} />
                        </Stack>
                    </>
                )}
                {dialog === 'install' && (
                    <>
                        <DialogHeader title="Installez Padelr pour réserver" onOpenChange={close} />
                        <Stack gap={4} hAlign="center" paddingBlock={4}>
                            <Text as="p" type="supporting" justify="center">
                                L'inscription aux cours se fait dans l'application. Installez-la,
                                puis revenez sur cette page et touchez à nouveau « Réserver dans l'app ».
                            </Text>
                            <StoreBadges appStoreURL={APP_STORE_URL} playStoreURL={PLAY_STORE_URL} />
                        </Stack>
                    </>
                )}
            </Dialog>
        </>
    )
}

BookingButton.propTypes = {
    seriesId: PropTypes.string.isRequired,
    isFull: PropTypes.bool,
}

export default BookingButton
